import { useState, useEffect, useCallback } from 'react';

// Hook for managing RV favorites with localStorage
export const useFavorites = () => {
  const [favorites, setFavorites] = useState([]);

  // Load favorites from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('rv_favorites');
      if (stored) {
        setFavorites(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Error loading favorites:', err);
      setFavorites([]);
    }
  }, []);

  // Save to localStorage whenever favorites change
  const saveFavorites = useCallback((newFavorites) => {
    try {
      localStorage.setItem('rv_favorites', JSON.stringify(newFavorites));
      setFavorites(newFavorites);
    } catch (err) {
      console.error('Error saving favorites:', err);
    }
  }, []);

  // Check if an RV is favorited
  const isFavorite = useCallback((rvId) => {
    return favorites.some(fav => fav.id === rvId);
  }, [favorites]);

  // Add to favorites
  const addFavorite = useCallback((rv) => {
    if (!rv || !rv.id) return false;
    
    const exists = favorites.some(fav => fav.id === rv.id);
    if (exists) return false;
    
    const newFavorite = {
      id: rv.id,
      name: rv.name || rv.title || `${rv.year || ''} ${rv.make || ''} ${rv.model || ''}`.trim(),
      year: rv.year,
      make: rv.make,
      model: rv.model,
      price: rv.price || rv.msrp,
      image: rv.image || rv.thumbnail || rv.imageUrl,
      url: rv.url || rv.link,
      savedAt: new Date().toISOString(),
    };
    
    const newFavorites = [...favorites, newFavorite];
    saveFavorites(newFavorites);
    return true;
  }, [favorites, saveFavorites]);

  // Remove from favorites
  const removeFavorite = useCallback((rvId) => {
    const newFavorites = favorites.filter(fav => fav.id !== rvId);
    saveFavorites(newFavorites);
    return true;
  }, [favorites, saveFavorites]);

  // Toggle favorite status
  const toggleFavorite = useCallback((rv) => {
    if (!rv || !rv.id) return false;
    
    if (isFavorite(rv.id)) {
      return removeFavorite(rv.id);
    } else {
      return addFavorite(rv);
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  // Clear all favorites
  const clearFavorites = useCallback(() => {
    saveFavorites([]);
  }, [saveFavorites]);

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearFavorites,
    count: favorites.length,
  };
};

export default useFavorites;
